import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ExternalLink, Star, ShoppingCart, Filter, Phone, 
  Bluetooth, Battery, Volume2, Eye, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// Hearing Aids data from HearClear.in
const hearingAids = [
  {
    id: "styletto-sha",
    name: "Signia Styletto AX",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹1,29,990",
    priceNum: 129990,
    emi: "₹43,330/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/image15.png",
    rating: 4.5,
    features: ["Sleek SLIM-RIC design", "Bluetooth streaming", "Rechargeable", "Own Voice Processing"],
    bestFor: "Style-conscious users wanting discreet, fashionable hearing aids",
    hearclearUrl: "https://hearclear.in/products/styletto-hearing-aid-sha",
    isBestSeller: true
  },
  {
    id: "insio-ite",
    name: "Signia Insio ITE",
    brand: "Signia",
    type: "Inside The Ear (ITE)",
    price: "₹25,990",
    priceNum: 25990,
    emi: "₹8,664/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/Insio-ITE-Nx_tan_1000x1000_86a8e866-3bf2-4fd8-91ec-d90bc9d969f0.png",
    rating: 4.5,
    features: ["Custom-fit to ear", "Nearly invisible", "Natural sound", "Easy to use"],
    bestFor: "Those wanting a discreet, in-the-ear solution",
    hearclearUrl: "https://hearclear.in/products/in-the-ear-ite-copy",
    isBestSeller: true
  },
  {
    id: "pure-1ix-pair",
    name: "Signia Pure C&G 1IX (Pair)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹1,44,990",
    priceNum: 144990,
    emi: "₹48,330/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Charge & Go", "Bluetooth connectivity", "IX platform", "App control"],
    bestFor: "Entry-level premium hearing aids with latest technology",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-1ix-kit-1",
    isBestSeller: true
  },
  {
    id: "pure-2ix-pair",
    name: "Signia Pure C&G 2IX (Pair)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹1,79,990",
    priceNum: 179990,
    emi: "₹59,997/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Advanced noise reduction", "Own Voice Processing", "Rechargeable", "Telehealth ready"],
    bestFor: "Users needing better speech understanding in noise",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-2ix-kit-1",
    isBestSeller: false
  },
  {
    id: "pure-3ix-pair",
    name: "Signia Pure C&G 3IX (Pair)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹3,69,990",
    priceNum: 369990,
    emi: "₹1,23,330/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Augmented Focus", "Motion sensors", "Superior streaming", "Own Voice Processing 2.0"],
    bestFor: "Active users who need premium performance in all environments",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-3ix-kit-copy-1",
    isBestSeller: false
  },
  {
    id: "pure-5ix-pair",
    name: "Signia Pure C&G 5IX (Pair)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹5,29,990",
    priceNum: 529990,
    emi: "₹1,76,664/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Top-tier technology", "Best-in-class streaming", "All environments", "Crystal clear speech"],
    bestFor: "Those wanting the best hearing experience money can buy",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-5ix-kit",
    isBestSeller: false
  },
  {
    id: "pure-7ix-pair",
    name: "Signia Pure C&G 7IX (Pair)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹7,99,990",
    priceNum: 799990,
    emi: "₹2,66,664/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Flagship technology", "Best Signia has to offer", "Premium everything", "Unmatched clarity"],
    bestFor: "Audiophiles and those demanding absolute best performance",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-7ix-kit",
    isBestSeller: false,
    isPremium: true
  },
  {
    id: "pure-1ix-single",
    name: "Signia Pure C&G 1IX (Single)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹79,990",
    priceNum: 79990,
    emi: "₹26,664/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Single ear solution", "Charge & Go", "Bluetooth", "App control"],
    bestFor: "Those needing hearing aid for one ear only",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-1ix",
    isBestSeller: false
  },
  {
    id: "pure-2ix-single",
    name: "Signia Pure C&G 2IX (Single)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹99,990",
    priceNum: 99990,
    emi: "₹33,330/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Single unit", "Own Voice Processing", "Rechargeable", "Better noise handling"],
    bestFor: "Single ear with mid-level features",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-2ix",
    isBestSeller: false
  },
  {
    id: "pure-3ix-single",
    name: "Signia Pure C&G 3IX (Single)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹1,89,990",
    priceNum: 189990,
    emi: "₹63,330/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Premium single unit", "Augmented Focus", "Motion sensors", "Superior streaming"],
    bestFor: "Premium single-ear solution",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-3ix",
    isBestSeller: false
  },
  {
    id: "pure-5ix-single",
    name: "Signia Pure C&G 5IX (Single)",
    brand: "Signia",
    type: "Receiver-In-Canal (RIC)",
    price: "₹2,79,990",
    priceNum: 279990,
    emi: "₹93,330/mo",
    image: "https://cdn.shopify.com/s/files/1/0777/2258/3276/files/images.jpg",
    rating: 4.5,
    features: ["Top-tier single", "Best streaming", "All environments", "Maximum clarity"],
    bestFor: "Best single-ear hearing aid",
    hearclearUrl: "https://hearclear.in/products/pure-c-g-5ix",
    isBestSeller: false
  }
];

// Hearing aid types
const types = [
  { id: "all", name: "All Types" },
  { id: "ric", name: "RIC (Behind Ear)" },
  { id: "ite", name: "ITE (In Ear)" },
  { id: "bte", name: "BTE (Behind Ear)" },
  { id: "cic", name: "CIC (Invisible)" },
];

// Price ranges
const priceRanges = [
  { id: "all", name: "All Prices" },
  { id: "under50k", name: "Under ₹50,000" },
  { id: "50k-1l", name: "₹50,000 - ₹1 Lakh" },
  { id: "1l-2l", name: "₹1 - 2 Lakhs" },
  { id: "2l-5l", name: "₹2 - 5 Lakhs" },
  { id: "above5l", name: "Above ₹5 Lakhs" },
];

function HearingAidCard({ product }) {
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow group">
      <div className="relative aspect-square bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <img
          src={product.image}
          alt={product.name}
          className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
        />
        {product.isBestSeller && (
          <Badge className="absolute top-3 left-3 bg-amber-500">Best Seller</Badge>
        )}
        {product.isPremium && (
          <Badge className="absolute top-3 left-3 bg-purple-600">Premium</Badge>
        )}
      </div>
      
      <CardContent className="p-4">
        <div className="flex items-center gap-1 mb-2">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className={`w-3 h-3 ${i < Math.floor(product.rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
          ))}
          <span className="text-xs text-slate-500 ml-1">{product.rating}</span>
        </div>
        
        <div className="text-xs text-sky-600 font-medium mb-1">{product.brand} • {product.type}</div>
        <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{product.name}</h3>
        
        <div className="mb-3">
          <div className="text-xl font-bold text-slate-900">{product.price}</div>
          <div className="text-xs text-slate-500">EMI from {product.emi} (No Cost)</div>
        </div>

        <div className="space-y-1 mb-4">
          {product.features.slice(0, 2).map((feature, i) => (
            <div key={i} className="text-xs text-slate-600 flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-sky-500" />
              {feature}
            </div>
          ))}
        </div>

        <a 
          href={product.hearclearUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <Button className="w-full bg-green-600 hover:bg-green-700">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Buy on HearClear
            <ExternalLink className="w-3 h-3 ml-2" />
          </Button>
        </a>
      </CardContent>
    </Card>
  );
}

export default function HearingAidsPage() {
  const [priceFilter, setPriceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");

  const filterByPrice = (product) => {
    if (priceFilter === "all") return true;
    const price = product.priceNum;
    switch (priceFilter) {
      case "under50k": return price < 50000;
      case "50k-1l": return price >= 50000 && price < 100000;
      case "1l-2l": return price >= 100000 && price < 200000;
      case "2l-5l": return price >= 200000 && price < 500000;
      case "above5l": return price >= 500000;
      default: return true;
    }
  };

  const sortProducts = (a, b) => {
    switch (sortBy) {
      case "price-low": return a.priceNum - b.priceNum;
      case "price-high": return b.priceNum - a.priceNum;
      case "rating": return b.rating - a.rating;
      default: return (b.isBestSeller ? 1 : 0) - (a.isBestSeller ? 1 : 0);
    }
  };

  const filteredProducts = hearingAids.filter(filterByPrice).sort(sortProducts);

  return (
    <div data-testid="hearing-aids-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-green-50 to-white py-16 md:py-20">
        <div className="container-width">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Badge className="mb-4 bg-green-100 text-green-700">Powered by HearClear.in</Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
                Premium Hearing Aids
              </h1>
              <p className="text-lg text-slate-600 mb-6">
                Explore top-quality hearing aids from Signia, ReSound, Widex, and more. 
                All products available for purchase on HearClear.in with EMI options, 
                3-year warranty, and 30-day trial.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Battery className="w-4 h-4 text-green-600" />
                  <span>Rechargeable</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Bluetooth className="w-4 h-4 text-green-600" />
                  <span>Bluetooth</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Eye className="w-4 h-4 text-green-600" />
                  <span>Invisible Options</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="https://hearclear.in/products" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-green-600 hover:bg-green-700">
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Shop All on HearClear
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Link to="/book-appointment">
                  <Button size="lg" variant="outline">
                    Get Expert Advice
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1632054224659-280be3239aff?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"
                alt="Modern hearing aids"
                className="w-full rounded-2xl shadow-xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg">
                <div className="text-2xl font-bold text-green-600">200+</div>
                <div className="text-sm text-slate-500">Products Available</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="bg-green-600 py-4">
        <div className="container-width">
          <div className="flex flex-wrap justify-center gap-6 text-white text-sm">
            <span>✓ Free Hearing Test</span>
            <span>✓ 30-Day Trial</span>
            <span>✓ 3-Year Warranty</span>
            <span>✓ No-Cost EMI</span>
            <span>✓ 50,000+ Happy Customers</span>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 md:top-20 z-30 bg-white border-b py-4">
        <div className="container-width">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-4 items-center">
              <Filter className="w-5 h-5 text-slate-400" />
              <Select value={priceFilter} onValueChange={setPriceFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Price Range" />
                </SelectTrigger>
                <SelectContent>
                  {priceRanges.map((range) => (
                    <SelectItem key={range.id} value={range.id}>{range.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4 items-center">
              <span className="text-sm text-slate-500">Sort by:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Rating</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-width">
          <div className="mb-6 flex justify-between items-center">
            <p className="text-slate-600">Showing {filteredProducts.length} products</p>
            <a 
              href="https://hearclear.in/products" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-green-600 hover:underline text-sm flex items-center gap-1"
            >
              View all 200+ products on HearClear
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <HearingAidCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* When Hearing Aids Are Not Enough */}
      <section className="bg-sky-50 py-16">
        <div className="container-width">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              When Hearing Aids Are Not Enough
            </h2>
            <p className="text-slate-600 mb-8">
              If you have severe to profound hearing loss and hearing aids no longer provide 
              adequate benefit, cochlear implants may be the solution. Dr. Sachdeva can evaluate 
              your candidacy and recommend the best path forward.
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <Link to="/cochlear-implant">
                <Button size="lg" className="bg-sky-600 hover:bg-sky-700">
                  Learn About Cochlear Implants
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/book-appointment">
                <Button size="lg" variant="outline">
                  Book Evaluation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Info */}
      <section className="bg-white py-12">
        <div className="container-width text-center">
          <p className="text-sm text-slate-500 mb-4">Hearing Aids powered by</p>
          <a href="https://hearclear.in" target="_blank" rel="noopener noreferrer" className="inline-block">
            <div className="text-2xl font-bold text-green-600 hover:text-green-700">
              HearClear.in
            </div>
            <div className="text-sm text-slate-500">India's #1 Trusted Hearing Aid Provider</div>
          </a>
          <div className="mt-6 flex justify-center gap-4 flex-wrap">
            <a href="tel:+919888438843" className="flex items-center gap-2 text-slate-600 hover:text-green-600">
              <Phone className="w-4 h-4" />
              98884 38843
            </a>
            <a 
              href="https://wa.me/919505540400?text=Hi!%20I%20want%20to%20know%20more%20about%20hearing%20aids." 
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:underline"
            >
              WhatsApp HearClear
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
